import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthLayout } from "../layouts/AuthLayout";
import { resetPassword } from "../../api/auth";
import { validatePassword } from "../../utils/passwordValidation";
import "./LoginPage.css";

type ResetPasswordPageProps = {
  token: string;
};

export function ResetPasswordPage({ token }: ResetPasswordPageProps) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  useEffect(() => {
    if (!token) {
      setErrorMessage("Lien de réinitialisation invalide.");
    }
  }, [token]);

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    const validation = validatePassword(value);
    setValidationErrors(validation.errors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage("");

    if (password !== confirmPassword) {
      setErrorMessage("Les mots de passe ne correspondent pas.");
      return;
    }

    const validation = validatePassword(password);
    if (!validation.valid) {
      setErrorMessage(validation.errors.join(" "));
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword(token, password);
      // Redirect to login with success message
      window.location.hash = "#/";
      // Could add a toast notification here
    } catch (error) {
      setErrorMessage((error as Error).message || "Échec de la réinitialisation");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout>
        <div className="login-page">
          <div className="login-header">
            <div className="login-brand">XLFlow</div>
            <h1>Lien invalide</h1>
            <p>Le lien de réinitialisation est incomplet. Vérifiez le lien reçu par email.</p>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <div className="login-page">
        <div className="login-header">
          <div className="login-brand">XLFlow</div>
          <h1>Réinitialiser votre mot de passe</h1>
          <p>Définissez un nouveau mot de passe pour votre compte.</p>
        </div>

        <form className="login-form" onSubmit={handleSubmit}>
          <label className="login-field">
            Nouveau mot de passe
            <input
              type="password"
              placeholder="12 caractères minimum"
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              required
              autoComplete="new-password"
              minLength={12}
            />
            {validationErrors.length > 0 && (
              <ul className="mt-1 text-xs text-danger list-disc list-inside">
                {validationErrors.map((error, idx) => (
                  <li key={idx}>{error}</li>
                ))}
              </ul>
            )}
          </label>

          <label className="login-field">
            Confirmer le mot de passe
            <input
              type="password"
              placeholder="Répétez votre mot de passe"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
              minLength={12}
            />
            {password && confirmPassword && password !== confirmPassword && (
              <p className="mt-1 text-xs text-danger">Les mots de passe ne correspondent pas.</p>
            )}
          </label>

          {errorMessage ? <p className="text-xs text-danger">{errorMessage}</p> : null}

          <button type="submit" className="login-primary" disabled={isSubmitting}>
            {isSubmitting ? "Réinitialisation..." : "Réinitialiser le mot de passe"}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
}
